import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { TabViewChangeEvent } from 'primeng/tabview';
import { forkJoin } from 'rxjs';
import { ClientSurveyTypeModel, EmployeeModel } from '../../api/models';
import { ClientRepService } from '../../api/services';
import { ClientsToCallGridComponent } from '../../components/grids/clients-to-call-grid/clients-to-call-grid.component';
import { ToastService } from '../../services/toast.service';
import { SurveyTypeService } from './../../api/services/survey-type.service';
import { QueryParamsService } from './../../services/query-params.service';

@Component({
  selector: 'app-clients-to-call',
  templateUrl: './clients-to-call.component.html',
  styleUrls: ['./clients-to-call.component.scss'],
})
export class ClientsToCallComponent implements OnInit {
  @ViewChildren(ClientsToCallGridComponent)
  grids: QueryList<ClientsToCallGridComponent>;

  dateForm: FormGroup;
  months: { id: number; name: string }[] = [
    { id: 1, name: 'January' },
    { id: 2, name: 'February' },
    { id: 3, name: 'March' },
    { id: 4, name: 'April' },
    { id: 5, name: 'May' },
    { id: 6, name: 'June' },
    { id: 7, name: 'July' },
    { id: 8, name: 'August' },
    { id: 9, name: 'September' },
    { id: 10, name: 'October' },
    { id: 11, name: 'November' },
    { id: 12, name: 'December' },
  ];
  years: number[] = [];
  selectedMonth = 1;
  selectedYear = 2025;
  areSurveyTypesLoading = false;
  surveyTypes: ClientSurveyTypeModel[] = [];
  reps: EmployeeModel[];

  // Query Params
  activeSurveyTypeId: number;
  activeIndex: number = 0;
  first: number = 0;
  rows: number = 20;
  attorneyName: string = '';
  matterName: string = '';
  matterNumber: string = '';
  searchRepId: number | undefined;

  faCircleNotch = faCircleNotch;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private toastService: ToastService,
    private surveyTypeService: SurveyTypeService,
    private clientRepService: ClientRepService,
    private QueryParamsService: QueryParamsService
  ) {}

  ngOnInit() {
    this.setUpDateForm();
    this.getAllSurveyData();
  }

  ngAfterViewInit() {
    // Listen for changes to the QueryList
    this.grids.changes.subscribe(
      (changes: QueryList<ClientsToCallGridComponent>) => {
        // Perform actions when the QueryList changes
        // Check for query parameters and set the active tab index
        this.route.queryParams.subscribe((params) => {
          // Save the params for future navigation purposes.
          const queryParams = { ...params['keys'], ...params };
          this.QueryParamsService.setQueryParams(queryParams); // Save the query params in the service for future reference.

          // Handle the month/year stuff.
          const month = parseInt(params['month'], 10);
          if (!isNaN(month)) {
            this.selectedMonth = month;
            this.dateForm.patchValue({
              month: this.selectedMonth,
            });
          }
          const year = parseInt(params['year'], 10);
          if (!isNaN(year)) {
            this.selectedYear = year;
            this.dateForm.patchValue({
              year: this.selectedYear,
            });
          }
          if (isNaN(month) || isNaN(year)) {
            this.setFormDefaults();
          }
          // Handle the grid pagination stuff.
          const surveyType = parseInt(params['surveyType'], 10);
          if (!isNaN(surveyType)) {
            this.activeSurveyTypeId = surveyType;
            this.activeIndex =
              this.grids !== undefined
                ? this.grids.find((x) => x.surveyType.id === surveyType)
                    ?.tabIndex || 0
                : 0;
          } else {
            this.activeSurveyTypeId = 8;
          }
          const first = parseInt(params['first'], 10);
          if (!isNaN(first)) {
            this.first = first;
          } else {
            this.first = 0;
          }
          const rows = parseInt(params['rows'], 10);
          if (!isNaN(rows)) {
            this.rows = rows;
          } else {
            this.rows = 20;
          }
          // Handle attorney name search parameter
          const attorneyName = params['attorneyName'];
          if (attorneyName) {
            this.attorneyName = attorneyName;
          } else {
            this.attorneyName = '';
          }
          // Handle matter name search parameter
          const matterName = params['matterName'];
          if (matterName) {
            this.matterName = matterName;
          } else {
            this.matterName = '';
          }
          // Handle matter number search parameter
          const matterNumber = params['matterNumber'];
          if (matterNumber) {
            this.matterNumber = matterNumber;
          } else {
            this.matterNumber = '';
          }
          // Handle rep search parameter
          const repId = parseInt(params['repId'], 10);
          if (!isNaN(repId)) {
            this.searchRepId = repId;
          } else {
            this.searchRepId = undefined;
          }
          this.loadActiveGrid();
        });
      }
    );
  }

  getClientReps() {
    return this.clientRepService.getClientReps$Json$Response();
  }

  setFormDefaults() {
    // Get the current date
    const currentDate = moment();

    // Extract the month and year
    const currentMonth = currentDate.month() + 1; // e.g., "1"
    const currentMonthYear = currentDate.format('YYYY'); // e.g., "2025"
    this.selectedMonth = currentMonth;
    this.selectedYear = parseInt(currentMonthYear);

    this.dateForm.patchValue({
      month: this.selectedMonth,
      year: this.selectedYear,
    });
  }

  setUpDateForm() {
    const currentYear = new Date().getFullYear();

    for (let year = currentYear; year >= currentYear - 5; year--) {
      this.years.push(year);
    }

    this.dateForm = this.fb.group({
      month: [this.selectedMonth, Validators.required],
      year: [this.selectedYear, Validators.required],
    });
  }

  getSurveyTypes() {
    this.areSurveyTypesLoading = true;

    return this.surveyTypeService.getSurveyTypes$Json$Response();
  }

  getAllSurveyData() {
    forkJoin({
      surveyTypes: this.surveyTypeService.getSurveyTypes$Json$Response(),
      reps: this.clientRepService.getClientReps$Json$Response(),
    }).subscribe({
      next: ({ surveyTypes, reps }) => {
        this.areSurveyTypesLoading = false;

        // Handle survey types response
        if (!surveyTypes.body) {
          console.error(surveyTypes.body);
          this.toastService.showToast('danger', 'Problem loading survey types');
        } else {
          this.surveyTypes = surveyTypes.body;
          this.activeSurveyTypeId = this.surveyTypes[0]?.id!;
        }

        // Handle reps response
        if (!reps.body) {
          console.error(reps.body);
          this.toastService.showToast('danger', 'Problem loading client reps');
        } else {
          this.reps = reps.body;
        }
        this.loadActiveGrid();
      },
      error: (error) => {
        this.areSurveyTypesLoading = false;
        console.error('Error loading data:', error);
        this.toastService.showToast('danger', 'Problem loading data');
      },
    });
  }

  loadActiveGrid() {
    if (!this.areSurveyTypesLoading) {
      // Trigger grid updates
      if (this.grids) {
        this.grids.forEach((grid) => {
          if (grid.surveyType.id === this.activeSurveyTypeId) {
            // Make sure the active index is set to the first grid.
            this.activeIndex = grid.tabIndex;
            grid.areClientsLoading = true;
            // Set search terms from query params
            grid.attorneyName = this.attorneyName;
            grid.matterName = this.matterName;
            grid.matterNumber = this.matterNumber;
            grid.searchRepId = this.searchRepId;
            grid.getClientsToCall(
              this.selectedYear,
              this.selectedMonth,
              this.reps,
              this.first,
              this.rows,
              this.attorneyName,
              this.matterName,
              this.matterNumber,
              this.searchRepId
            );
          }
        });
      }
    }
  }

  dateFormOnSubmit() {
    if (this.dateForm.valid) {
      const selectedMonth = this.dateForm.get('month')?.value;
      const selectedYear = this.dateForm.get('year')?.value;
      this.selectedMonth = selectedMonth;
      this.selectedYear = selectedYear;

      // Update the URL with the new date info
      this.router.navigate([], {
        queryParams: {
          month: this.selectedMonth,
          year: this.selectedYear,
        },
        queryParamsHandling: 'merge', // Merge with existing query params
      });
    }
  }

  onTabChange(event: TabViewChangeEvent): void {
    // Find the surveyType that corresponds with the tabIndex.
    this.activeSurveyTypeId = this.grids.find(
      (x) => x.tabIndex === event.index
    )?.surveyType.id!;

    // Update the URL with the new tab index
    this.router.navigate([], {
      queryParams: {
        surveyType: this.activeSurveyTypeId,
        first: 0,
        rows: 20,
      },
      queryParamsHandling: 'merge', // Merge with existing query params
    });
  }
}
